import { OrderGuideBatchUpdateResult } from '@choco-admin/types';
import { useCallback } from 'react';

import { useOrderGuideUploadPageOrderGuideBatchUpdateMutation } from '../../../generated';
import {
  BatchUpdateParams,
  buildBatchUpdateInput,
  mapBatchUpdateResponse,
} from '../utils/mapping';

/**
 * Wraps orderGuideV2BatchUpdate. Never throws — every failure mode is
 * folded into `{ ok: false, errorMessage }` so the upload loop can report
 * per-group outcomes.
 */
export const useOrderGuideBatchUpdate = () => {
  const [mutate, { loading: isUpdating }] =
    useOrderGuideUploadPageOrderGuideBatchUpdateMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  const batchUpdate = useCallback(
    async (params: BatchUpdateParams): Promise<OrderGuideBatchUpdateResult> => {
      try {
        const { data, errors } = await mutate({
          variables: { input: buildBatchUpdateInput(params) },
        });
        if (!data?.orderGuideV2BatchUpdate) {
          return {
            ok: false,
            errorMessage:
              errors?.[0]?.message ?? 'No response from the backend.',
          };
        }
        return mapBatchUpdateResponse(data.orderGuideV2BatchUpdate);
      } catch (mutationError) {
        return {
          ok: false,
          errorMessage:
            mutationError instanceof Error
              ? mutationError.message
              : 'The upload request failed.',
        };
      }
    },
    [mutate]
  );

  return { batchUpdate, isUpdating };
};
